import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Hourglass, CheckCircle, XCircle, Stethoscope, MagnifyingGlass, Users, MapPin } from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/api";
import AddWalkinDialog from "@/components/AddWalkinDialog";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { shareAppointmentOnWhatsApp } from "@/lib/whatsapp";
import { InlineLoader } from "@/components/LoadingSkeleton";

const STATUS_META = {
  waiting: { label: "Waiting", color: "#6B7F74", icon: Hourglass, bg: "#F2EFE9" },
  in_progress: { label: "In progress", color: "#C57053", icon: Stethoscope, bg: "#FBE8DF" },
  completed: { label: "Completed", color: "#224032", icon: CheckCircle, bg: "#E1ECE5" },
  cancelled: { label: "Cancelled", color: "#8A2A2A", icon: XCircle, bg: "#F5E1E1" },
};

export default function DoctorAppointments() {
  const [searchParams] = useSearchParams();
  const filterParam = searchParams.get("filter") || "all";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [walkinOpen, setWalkinOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      let all = [];
      if (filterParam === "future") {
        const res = await api.get("/appointments/future");
        all = res.data.appointments || [];
      } else {
        const res = await api.get("/appointments/all");
        all = res.data.appointments || [];
      }
      all.sort((a, b) => new Date(b.created_at || b.queue_date) - new Date(a.created_at || a.queue_date));
      setItems(all);
    } catch (err) {
      toast.error("Could not load appointments");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParam]);

  const shareWhatsApp = async (appointment) => {
    try {
      await shareAppointmentOnWhatsApp(appointment, { sourcePage: "doctor_appointments" });
      toast.success("WhatsApp message opened");
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not prepare WhatsApp message");
    }
  };

  const filtered = items.filter((a) => {
    if (statusFilter !== "all" && a.status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchName = a.patient_name?.toLowerCase().includes(q);
      const matchReason = a.reason?.toLowerCase().includes(q);
      const matchToken = String(a.token_number || "").includes(q);
      if (!matchName && !matchReason && !matchToken) return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-background">
      <main className="workspace-page-flush py-3 sm:py-4">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Appointments</div>
          <h1 className="font-display font-bold text-foreground text-3xl sm:text-4xl lg:text-5xl tracking-tight">
            {filterParam === "future" ? "Future appointments" : "All appointments"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">{filtered.length} appointment{filtered.length !== 1 ? "s" : ""}</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <MagnifyingGlass size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by name, reason, token…" className="h-10 pl-9 pr-4 rounded-full text-sm" />
          </div>
          <div className="inline-flex rounded-full border border-border bg-card p-1">
            {["all", "waiting", "in_progress", "completed", "cancelled"].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(s)}
                className={`text-sm px-3 py-1.5 rounded-full whitespace-nowrap ${statusFilter === s ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                {s === "all" ? "All" : STATUS_META[s]?.label || s}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <InlineLoader />
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground">
            No appointments found.
          </div>
        ) : (
          <div className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="divide-row">
              {filtered.map((a) => {
                const meta = STATUS_META[a.status] || STATUS_META.cancelled;
                const Icon = meta.icon;
                return (
                  <div key={a.id} className="flex flex-wrap items-center gap-4 px-5 sm:px-6 py-4">
                    <div className="font-token text-2xl sm:text-3xl text-primary w-16 shrink-0">
                      #{String(a.token_number).padStart(2, "0")}
                    </div>
                    <div className="flex-1 min-w-[180px]">
                      <div className="font-display font-semibold text-foreground">{a.patient_name}</div>
                      <div className="text-sm text-muted-foreground line-clamp-1">{a.reason}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{a.queue_date}</div>
                      {a.clinic_name_snapshot && (
                        <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                          <MapPin size={10} weight="duotone" />
                          <span>{a.clinic_name_snapshot}{a.clinic_address_snapshot ? ` — ${a.clinic_address_snapshot}` : ""}</span>
                        </div>
                      )}
                    </div>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ backgroundColor: meta.bg, color: meta.color }}>
                      <Icon size={14} weight="duotone" />
                      <span className="text-xs font-semibold uppercase tracking-wider">{meta.label}</span>
                    </div>
                    {a.status === "cancelled" && (a.cancelled_by || a.cancellation_reason) && (
                      <div className="inline-flex items-center gap-1.5 bg-red-50 border border-red-200 rounded-xl px-3 py-1.5 text-xs">
                        {a.cancelled_by === "doctor" ? <span className="font-bold text-red-800">Cancelled by doctor</span> : a.cancelled_by === "patient" ? <span className="font-bold text-red-800">Cancelled by patient</span> : null}
                        {a.cancellation_reason ? <span className="text-red-600">· {a.cancellation_reason}</span> : null}
                      </div>
                    )}
                    {a.patient_phone && a.status !== "cancelled" && a.status !== "no_show" && (
                      <button
                        type="button"
                        onClick={() => shareWhatsApp(a)}
                        title="Send WhatsApp queue link"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-primary/15 text-primary hover:bg-primary/10"
                      >
                        <WhatsAppIcon size={17} />
                      </button>
                    )}
                    {a.is_follow_up && (
                      <div className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-green-50 text-green-700 text-xs font-semibold">
                        Follow-up
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      {/* FAB */}
      <button
        type="button"
        onClick={() => setWalkinOpen(true)}
        className="fixed bottom-6 left-6 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-[#1a3025] transition-colors flex items-center justify-center"
        title="New appointment"
      >
        <Users size={24} weight="duotone" />
      </button>

      <AddWalkinDialog open={walkinOpen} onClose={() => { setWalkinOpen(false); load(); }} />
    </div>
  );
}
